'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState, useTransition } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { submitContactForm } from './actions/contact';
interface FormState {
  fullName: string;
  email: string;
  goals: string;
  phone: string;
  startDate: string;
  availableTime: string;
}
const initialFormState: FormState = {
  fullName: '',
  email: '',
  goals: '',
  phone: '',
  startDate: '',
  availableTime: ''
};
function ContactFormInner() {
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [isPending, startTransition] = useTransition();
  const {
    executeRecaptcha
  } = useGoogleReCaptcha();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = useCallback(async (e_0: React.FormEvent<HTMLFormElement>) => {
    e_0.preventDefault();
    setMessage(null);
    if (!executeRecaptcha) {
      setMessage({
        type: 'error',
        text: 'reCAPTCHA not loaded. Please refresh and try again.'
      });
      return;
    }
    startTransition(async () => {
      try {
        const recaptchaToken = await executeRecaptcha('contact_form');
        const result = await submitContactForm({
          ...formData,
          recaptchaToken
        });
        setMessage({
          type: result.success ? 'success' : 'error',
          text: result.message
        });
        if (result.success) {
          setFormData(initialFormState);
        }
      } catch {
        setMessage({
          type: 'error',
          text: 'An unexpected error occurred. Please try again.'
        });
      }
    });
  }, [executeRecaptcha, formData]);
  return <form onSubmit={handleSubmit} className="space-y-[24px]">
      <div>
        <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} disabled={isPending} className="w-full rounded-[8px] border border-[#1a2d4d] bg-[#0a1628] px-[20px] py-[16px] text-[16px] text-FFFFFF placeholder:text-[#D5D9DD] focus:border-[#27D3ED] focus:outline-none disabled:opacity-50" required />
      </div>
      <div>
        <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} disabled={isPending} className="w-full rounded-[8px] border border-[#1a2d4d] bg-[#0a1628] px-[20px] py-[16px] text-[16px] text-FFFFFF placeholder:text-[#D5D9DD] focus:border-[#27D3ED] focus:outline-none disabled:opacity-50" required />
      </div>
      <div>
        <textarea name="goals" placeholder="Your Goals / Concerns" rows={5} value={formData.goals} onChange={handleChange} disabled={isPending} className="w-full rounded-[8px] border border-[#1a2d4d] bg-[#0a1628] px-[20px] py-[16px] text-[16px] text-FFFFFF placeholder:text-[#D5D9DD] focus:border-[#27D3ED] focus:outline-none disabled:opacity-50" required></textarea>
      </div>
      <div>
        <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} disabled={isPending} className="w-full rounded-[8px] border border-[#1a2d4d] bg-[#0a1628] px-[20px] py-[16px] text-[16px] text-FFFFFF placeholder:text-[#D5D9DD] focus:border-[#27D3ED] focus:outline-none disabled:opacity-50" required />
      </div>
      <div>
        <input type="text" name="startDate" placeholder="Preferred Start Date" value={formData.startDate} onChange={handleChange} disabled={isPending} className="w-full rounded-[8px] border border-[#1a2d4d] bg-[#0a1628] px-[20px] py-[16px] text-[16px] text-FFFFFF placeholder:text-[#D5D9DD] focus:border-[#27D3ED] focus:outline-none disabled:opacity-50" required />
      </div>
      <div>
        <input type="text" name="availableTime" placeholder="Available Time for a Call" value={formData.availableTime} onChange={handleChange} disabled={isPending} className="w-full rounded-[8px] border border-[#1a2d4d] bg-[#0a1628] px-[20px] py-[16px] text-[16px] text-FFFFFF placeholder:text-[#D5D9DD] focus:border-[#27D3ED] focus:outline-none disabled:opacity-50" required />
      </div>

      {message && <div className={`rounded-[8px] px-[20px] py-[16px] text-[16px] ${message.type === 'success' ? 'border-green-500/30 bg-green-500/10 text-green-400 border' : 'border-red-500/30 bg-red-500/10 text-red-400 border'}`}>
          {message.text}
        </div>}

      <button type="submit" disabled={isPending} className="h-auto min-h-[56px] w-full rounded-[8px] bg-linear-to-r from-[#88EFFF] to-[#27D3ED] py-[12px] text-[16px] font-semibold text-[#020e20] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 desktop:w-[320px] desktop:text-[18px]">
        {isPending ? 'Sending...' : 'Send Message'}
      </button>

      <p className="text-[12px] text-[#8899aa]">
        This site is protected by reCAPTCHA and the Google{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#27D3ED]">
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#27D3ED]">
          Terms of Service
        </a>{' '}
        apply.
      </p>
    </form>;
}
export default function ContactForm() {
  const $ = _c(2);
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";
  if (!siteKey) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <ContactFormInner />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <GoogleReCaptchaProvider reCaptchaKey={siteKey}><ContactFormInner /></GoogleReCaptchaProvider>;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}